import type { SettingsSection } from '#layers/global-settings/constants/settings-overlay-panels';
import { computed } from 'vue';
import { useRoute, useRouter } from '#app/composables/router';
import { SETTINGS_QUERY_KEY } from '#layers/global-settings/constants/settings-overlay';

/** The overlay section lives in `?settings=<group>/<section>`; the hash is a native in-panel anchor. */
export function useSettingsOverlay() {
	const route = useRoute();
	const router = useRouter();

	const activeSection = computed(() => {
		const value = route.query[SETTINGS_QUERY_KEY];
		return typeof value === 'string' && value.length > 0 ? value : null;
	});
	const isOpen = computed(() => activeSection.value !== null);

	function queryWith(section?: SettingsSection) {
		const query = { ...route.query };
		if (section === undefined) delete query[SETTINGS_QUERY_KEY];
		else query[SETTINGS_QUERY_KEY] = section;
		return query;
	}

	function locationFor(section?: SettingsSection) {
		return { path: route.path, query: queryWith(section), hash: '' };
	}

	function open(section: SettingsSection) {
		// Re-triggering while already open (e.g. the ⌘⇧O shortcut) should switch section, not stack a
		// second `?settings=` entry that would linger and reopen the overlay after close() steps back.
		if (isOpen.value) return navigate(section);
		router.push(locationFor(section));
	}

	function navigate(section: SettingsSection) {
		router.replace(locationFor(section));
	}

	function close() {
		const back = (window.history.state as { back?: unknown } | null)?.back;
		if (typeof back === 'string') router.back();
		else router.replace(locationFor());
	}

	return { activeSection, isOpen, open, navigate, close };
}
